import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

/* ── Bootstrap numbered pagination ── */
function Pagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>

        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
                disabled={disabled}
              >
                {p}
              </button>
            </li>
          ),
        )}

        <li
          className={`page-item ${page >= totalPages || disabled ? "disabled" : ""}`}
        >
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminWalletTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [statusMap, setStatusMap] = useState({});

  /* filter — separate input from applied to fix the clear bug */
  const [userIdInput, setUserIdInput] = useState("");
  const [appliedUserId, setAppliedUserId] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0); // debit-only total from API

  const adminToken = localStorage.getItem("adminToken");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / limit)),
    [total, limit],
  );

  const fetchWalletTransactions = async (
    nextPage = 1,
    userId = appliedUserId,
  ) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
        type: "debit", // ← tell API to return only debit records
      });
      if (userId.trim()) qs.set("userId", userId.trim());

      const res = await fetch(`${API_BASE}/Wallet?${qs.toString()}`, {
        headers: { Authorization: adminToken },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      // API may still return mixed types if it ignores the param — filter client-side as safety net
      const all = result?.transactions || [];
      const debits = all.filter((t) => t.type === "debit");

      setTransactions(debits);
      // Prefer API-provided total; fall back to counting what we got
      setTotal(result?.total ?? result?.debitTotal ?? debits.length);
      setPage(result?.page || nextPage);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch wallet transactions.");
    } finally {
      setLoading(false);
    }
  };

  /* initial load */
  useEffect(() => {
    fetchWalletTransactions(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedUserId(userIdInput);
    setPage(1);
    fetchWalletTransactions(1, userIdInput);
  };

  const handleClear = () => {
    setUserIdInput("");
    setAppliedUserId("");
    setPage(1);
    fetchWalletTransactions(1, "");
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchWalletTransactions(p, appliedUserId);
  };

  const handleStatusChange = (id, value) => {
    setStatusMap((prev) => ({ ...prev, [id]: value }));
  };

  const updateStatus = async (id) => {
    const status = statusMap[id];
    if (!status) {
      alert("Please select a status first.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/Withdrawal`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken,
        },
        body: JSON.stringify({ transactionId: id, status }),
      });

      const text = await res.text();

      if (res.ok) {
        alert("Status updated ✅");
        fetchWalletTransactions(page, appliedUserId);
      } else {
        alert(text || "Update failed ❌");
      }
    } catch (err) {
      console.error(err);
      alert("Network error ❌");
    }
  };

  /* row range for "Showing X–Y" */
  const rangeStart = transactions.length ? (page - 1) * limit + 1 : 0;
  const rangeEnd = Math.min(page * limit, total);

  return (
    <>
      <Header />

      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header Row */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    Withdrawal Requests ({total})
                  </h3>
                  <div className="text-muted">
                    Debit Transactions / Approve or Decline Withdrawals
                  </div>
                </div>

                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={() => fetchWalletTransactions(page, appliedUserId)}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={userIdInput}
                          onChange={(e) => setUserIdInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by User ID…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading || (!userIdInput && !appliedUserId)}
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* Table */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm" />
                            </td>
                          </tr>
                        ) : transactions.length === 0 ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5 text-muted"
                            >
                              No debit transactions found.
                            </td>
                          </tr>
                        ) : (
                          transactions.map((t, idx) => (
                            <tr key={t.transactionId || idx}>
                              <td className="fw-semibold">
                                {(page - 1) * limit + idx + 1}.
                              </td>

                              <td>
                                <div className="lh-sm my-2">
                                  <div>
                                    <span className="text-muted">
                                      Transaction ID
                                    </span>{" "}
                                    —{" "}
                                    <span className="fw-semibold">
                                      {t.transactionId}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">User ID</span>{" "}
                                    — {t.userId}
                                  </div>
                                  <div>
                                    <span className="text-muted">Name</span>{" "}
                                    — {t.name}
                                  </div>
                                  <div>
                                    <span className="text-muted">Type</span> —{" "}
                                    <span className="badge bg-danger text-capitalize">
                                      {t.type}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">Amount</span> —
                                    ₹{t.amount}
                                  </div>
                                  <div>
                                    <span className="text-muted">Source</span> —{" "}
                                    {t.source || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Created At
                                    </span>{" "}
                                    — {formatDate(t.createdAt)}
                                  </div>
                                  <hr className="w-25" />
                                  <div>
                                    <span className="text-muted">
                                      Name as per Bank
                                    </span>{" "}
                                    — {t?.nameOnBank}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Bank Account No.
                                    </span>{" "}
                                    — {t?.bankAccountNo}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      IFSC Code
                                    </span>{" "}
                                    — {t?.ifsc}
                                  </div>
                                  <div>
                                    <span className="text-muted">UPI ID</span> —{" "}
                                    {t?.upiId}
                                  </div>
                                  <hr className="w-25" />
                                  <div>
                                    <span className="text-muted">Status</span> —{" "}
                                    <span
                                      className={`badge text-capitalize ${
                                        t.status === "approved"
                                          ? "bg-success"
                                          : t.status === "rejected"
                                            ? "bg-danger"
                                            : "bg-warning text-dark"
                                      }`}
                                    >
                                      {t.status}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <div className="d-flex flex-column gap-2 align-items-start py-2">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: 200 }}
                                    value={statusMap[t.transactionId] || ""}
                                    onChange={(e) =>
                                      handleStatusChange(
                                        t.transactionId,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Select</option>
                                    <option value="approved">Approve</option>
                                    <option value="rejected">Decline</option>
                                  </select>

                                  <button
                                    className="btn btn-success btn-sm px-4 fw-medium rounded-pill"
                                    onClick={() =>
                                      updateStatus(t.transactionId)
                                    }
                                  >
                                    Update Status
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {rangeStart}–{rangeEnd} of {total} records
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminWalletTransactions;
